/**
 * Catalog register (§23c) — the set of Oxbo-M master catalogs the picklists
 * bind to, grouped exactly as the Oxbo-M catalog-admin chrome shows them
 * (Operations · Cost · Equipment & tubulars · Fluids & other).
 *
 * This is a *browse index only*. Oxbo-Z owns none of this data — each catalog's
 * values (and its `admin` descriptor) come from `GET /api/m/catalogs/{key}`.
 * The register just tells the preview which keys exist and how to label/group
 * them so the read-only screen mirrors what the real Oxbo-M admin looks like.
 * The `label` here is a fallback caption; the live `label` from the payload
 * wins once a catalog is loaded.
 */

/** A catalog's declared shape (§23a) — drives which attribute columns show. */
export type CatalogShape = "flat" | "tree" | "attributed";

export interface CatalogRegisterEntry {
  /** Catalog key — the `{key}` in GET /api/m/catalogs/{key}. */
  key: string;
  /** Human label (fallback until the payload's own label loads). */
  label: string;
  /** Declared shape — hints the column set before the payload arrives. */
  shape: CatalogShape;
  /** Seed provenance note (Aura reporting_cp_* / EDM / standard) — display only. */
  seed?: string;
}

export interface CatalogGroup {
  /** Group heading, mirroring the Oxbo-M admin nav sections. */
  titleKey: "catadmin.group.operations" | "catadmin.group.cost" | "catadmin.group.equipment" | "catadmin.group.fluids";
  entries: CatalogRegisterEntry[];
}

/**
 * The grouped register. Keys match the CATALOG_VALUES mock (and the live
 * contract). Screens fetch per-key; this list only structures the browse nav.
 */
export const CATALOG_REGISTER: CatalogGroup[] = [
  {
    titleKey: "catadmin.group.operations",
    entries: [
      { key: "activity_codes", label: "Activity / step codes", shape: "attributed", seed: "Aura reporting_cp_wbs_codigos" },
      { key: "hse_categories", label: "HSE categories", shape: "flat", seed: "HSE master" },
      { key: "survey_tools", label: "Survey tools", shape: "flat", seed: "standard" },
    ],
  },
  {
    titleKey: "catadmin.group.cost",
    entries: [
      { key: "cost_accounts", label: "Cost accounts", shape: "attributed", seed: "Aura reporting_cp_cuentas_contables" },
    ],
  },
  {
    titleKey: "catadmin.group.equipment",
    entries: [
      { key: "string_types", label: "String types", shape: "flat", seed: "standard" },
      { key: "nominal_sizes", label: "OD / hole sizes", shape: "attributed", seed: "EDM (numeric cleanup)" },
      { key: "component_types", label: "Tubular / BHA component types", shape: "flat", seed: "EDM cd_assembly_comp" },
      { key: "grades", label: "Pipe grades", shape: "flat", seed: "EDM GRADE (dedup)" },
      { key: "connections", label: "Connections", shape: "flat", seed: "EDM CONNECTION (dedup)" },
    ],
  },
  {
    titleKey: "catadmin.group.fluids",
    entries: [
      { key: "mud_products", label: "Mud products", shape: "attributed", seed: "mud master; EDM dm_mud_product" },
    ],
  },
];

/** Flat lookup of every registered catalog by key. */
export const CATALOG_BY_KEY: Record<string, CatalogRegisterEntry> = Object.fromEntries(
  CATALOG_REGISTER.flatMap((g) => g.entries).map((e) => [e.key, e]),
);

/** The first catalog to open by default when the screen mounts. */
export const DEFAULT_CATALOG_KEY = "cost_accounts";
